import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  NoPreloading,
  PreloadAllModules,
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading
} from '@angular/router';

import { provideAuth0 } from '@auth0/auth0-angular';
import { provideTransloco } from '@jsverse/transloco';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideStore } from '@ngxs/store';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { NzModalModule } from 'ng-zorro-antd/modal';

import * as LinkShortenerApiModule from '@link-shortener-api/api.module';

import * as SmartHeadApiModule from '@smarthead-api/api.module';

import { APP_ROUTES } from './app.routes';
import { PATHS_DICT, ROUTES_PREFIX } from './auth/auth.constants';
import { AdminTitleStrategy } from './shared/core/admin-title-strategy';
import { AdminTranslocoLoader } from './shared/i18n/admin-transloco-loader';
import { JWTTokenInterceptor } from './shared/interceptors/jwt-token.interceptor';
import { AppState } from './shared/state/app.state';

import * as AuditLogApiConfiguration from '../../../api/audit-log/api-configuration';
import * as PredictionConfiguration from '../../../api/prediction-api/api-configuration';
import { environment } from '../environments/environment';

/**
 * Setup base url by environment for Swagger API
 */
const APIV2BaseURL = (): AuditLogApiConfiguration.ApiConfiguration => {
  const appConfiguration = new AuditLogApiConfiguration.ApiConfiguration();
  appConfiguration.rootUrl = `${environment.api_smarthead_v2_base_path}`;
  return appConfiguration;
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      APP_ROUTES,
      withPreloading(environment.production ? PreloadAllModules : NoPreloading),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      withComponentInputBinding()
    ),
    { provide: TitleStrategy, useClass: AdminTitleStrategy },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: JWTTokenInterceptor, multi: true }, // add JWT token to requests

    // *** APIs ***
    importProvidersFrom(
      SmartHeadApiModule.ApiModule.forRoot({
        rootUrl: environment.api_smarthead_base_path
      }),
      LinkShortenerApiModule.ApiModule.forRoot({
        rootUrl: environment.api_link_shortener_base_path
      })
    ),
    { provide: AuditLogApiConfiguration.ApiConfiguration, useFactory: APIV2BaseURL },
    { provide: PredictionConfiguration.ApiConfiguration, useFactory: APIV2BaseURL },

    // *** Auth ***
    provideAuth0({
      clientId: environment.auth0_clientId,
      domain: environment.auth0_domain,
      errorPath: PATHS_DICT.ERROR,
      // Refresh token is kept in localStorage rather than in memory so the session
      // survives a full page reload (ng serve rebuilds, manual refresh). With the
      // default 'memory' cache the SDK falls back to silent auth in an iframe, which
      // modern browsers block via third-party cookie restrictions -> user is logged out.
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      authorizationParams: {
        redirect_uri: new URL(`${ROUTES_PREFIX}/${PATHS_DICT.CALLBACK}`, window.location.origin).href
      }
    }),

    // *** State ***
    provideStore([AppState], { developmentMode: !environment.production }, withNgxsReduxDevtoolsPlugin({ disabled: environment.production })),

    // *** UI: ng-zorro and ui-platform ***
    { provide: NZ_I18N, useValue: en_US },
    importProvidersFrom(NzModalModule), // NzModalService of the confirm dialog
    provideTransloco({
      config: {
        availableLangs: ['en'],
        defaultLang: 'en',
        reRenderOnLangChange: false,
        prodMode: environment.production,
        missingHandler: {
          logMissingKey: false // the ui-platform components pass the admin texts through transloco too
        }
      },
      loader: AdminTranslocoLoader
    })
  ]
};
